import depsloader from "../../../depsloader";

const SEARCH_URL = "https://api.screenwork.de/geocoder/search";
const REVERSE_URL = "https://api.screenwork.de/geocoder/reverse";
const TIMEOUT_MS = 20000;

const DISTRICT_PLACES = ["suburb", "quarter", "neighbourhood", "city_district", "borough"];
const CITY_PLACES = ["city", "town", "village", "hamlet", "municipality"];

// Der Maps-Key wird serverseitig je Request in data-maps-key ausgegeben.
function mapsKey() {
  if (typeof document === "undefined" || !document.documentElement || !document.documentElement.dataset) {
    return "";
  }

  const key = document.documentElement.dataset.mapsKey;
  return typeof key === "string" ? key.trim() : "";
}

function text(value) {
  if (typeof value === "number" && isFinite(value)) {
    return String(value);
  }

  return typeof value === "string" && value.trim() !== "" ? value.trim() : null;
}

function validCoordinates(lat, lng) {
  return typeof lat === "number" && typeof lng === "number"
    && isFinite(lat) && isFinite(lng)
    && lat >= -90 && lat <= 90
    && lng >= -180 && lng <= 180;
}

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.searchSeq = 0;
    this.searchSource = null;
  }

  // Beendet die laufende Suche; ihr Promise endet mit null.
  cancelAutocomplete() {
    this.searchSeq++;

    if (this.searchSource != null) {
      this.searchSource.cancel();
      this.searchSource = null;
    }
  }

  _geoaddressText(g, name) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    } else if (g.zip && g.district) {
      s = g.zip + " " + g.district;
    } else if (g.district) {
      s = g.district;
    }

    const p = [];

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    if (s) {
      p.push(s);
    }

    // Ohne Orts- und Straßenangabe bleibt der Name als Beschriftung erhalten.
    if (p.length === 0 && name) {
      p.push(name);
    }

    if (g.state) {
      p.push(g.state);
    }

    if (g.country) {
      p.push(g.country);
    }

    return p.join(", ");
  }

  _featureType(p) {
    const type = text(p.type);
    if (type) {
      return type;
    }

    // Fallback für Antworten ohne properties.type
    if (p.osm_key == "highway") {
      return "street";
    }
    if (p.osm_key == "boundary") {
      return "district";
    }
    if (p.osm_key == "place") {
      if (DISTRICT_PLACES.indexOf(p.osm_value) > -1) {
        return "district";
      }
      if (CITY_PLACES.indexOf(p.osm_value) > -1) {
        return "city";
      }
      return "locality";
    }

    return text(p.housenumber) ? "house" : "other";
  }

  _geoaddress(feature) {
    if (!feature || typeof feature !== "object") {
      return null;
    }

    const p = feature.properties && typeof feature.properties === "object" ? feature.properties : {};
    const coordinates = feature.geometry && feature.geometry.coordinates;
    if (!Array.isArray(coordinates) || coordinates.length < 2) {
      return null;
    }

    // GeoJSON: [lng, lat]
    const lng = Number(coordinates[0]);
    const lat = Number(coordinates[1]);
    if (!validCoordinates(lat, lng)) {
      return null;
    }

    const type = this._featureType(p);
    const name = text(p.name);
    const geoaddress = {};

    let street = text(p.street);
    if (!street && type == "street") {
      street = name;
    }
    if (street) {
      geoaddress["street"] = street;
      const number = text(p.housenumber);
      if (number && type != "street") {
        geoaddress["number"] = number;
      }
    }

    const zip = text(p.postcode);
    if (zip) {
      geoaddress["zip"] = zip;
    }

    let city = text(p.city);
    if (!city && type == "city") {
      city = name;
    }
    if (city) {
      geoaddress["city"] = city;
    }

    let district = text(p.district);
    if (!district && type == "district") {
      district = name;
    }
    if (district && district != city) {
      geoaddress["district"] = district;
    }

    geoaddress["lat"] = lat;
    geoaddress["lng"] = lng;

    geoaddress["state"] = text(p.state) || undefined;
    geoaddress["country"] = text(p.country) || undefined;

    geoaddress["text"] = this._geoaddressText(geoaddress, name);
    if (!geoaddress["text"]) {
      return null;
    }

    return geoaddress;
  }

  _features(res) {
    return res && res.data && Array.isArray(res.data.features) ? res.data.features : [];
  }

  // Liefert Treffer, oder null, wenn die Suche durch eine neuere ersetzt oder abgebrochen wurde.
  autocomplete(q) {
    this.cancelAutocomplete();

    const query = typeof q === "string" ? q.trim() : "";
    const key = mapsKey();
    if (!query || !key) {
      return Promise.resolve([]);
    }

    const seq = this.searchSeq;
    const source = this.axios.CancelToken.source();
    this.searchSource = source;

    const params = new URLSearchParams();
    params.append("q", query);
    params.append("lang", "de");
    params.append("limit", 10);
    params.append("key", key);

    return this.axios.get(SEARCH_URL, {
      cancelToken: source.token,
      timeout: TIMEOUT_MS,
      params
    }).then(res => {
      if (seq !== this.searchSeq) {
        return null;
      }

      const results = [];
      this._features(res).forEach(feature => {
        const geoaddress = this._geoaddress(feature);
        if (geoaddress) {
          results.push(geoaddress);
        }
      });

      return results;
    }).catch(thrown => {
      if (this.axios.isCancel(thrown) || seq !== this.searchSeq) {
        return null;
      }

      throw thrown;
    }).finally(() => {
      if (this.searchSource === source) {
        this.searchSource = null;
      }
    });
  }

  reverse(lat, lng) {
    const latitude = Number(lat);
    const longitude = Number(lng);
    if (!validCoordinates(latitude, longitude)) {
      return Promise.reject(new Error("invalid coordinates"));
    }

    const key = mapsKey();
    if (!key) {
      return Promise.reject(new Error("maps key missing"));
    }

    return this.axios.get(REVERSE_URL, {
      timeout: TIMEOUT_MS,
      params: {
        lat: latitude,
        lon: longitude,
        lang: "de",
        key: key
      }
    }).then(res => {
      const features = this._features(res);
      const geoaddress = features.length > 0 ? this._geoaddress(features[0]) : null;
      if (!geoaddress) {
        throw new Error("invalid data");
      }

      return geoaddress;
    });
  }
}

function GeocoderFactory() {
  return new Promise(resolve => {
    depsloader.load("axios").then(axios => {
      resolve(new Geocoder(axios));
    })
  });
}

export { GeocoderFactory };

export default {};
